/*
 * ---------------------------------------------------------------------------
 * sm-facets.js — Shredder Master spec-level collection filtering
 * ---------------------------------------------------------------------------
 *
 * WHY THIS IS CLIENT SIDE
 * Shopify storefront filters are configured in the Search & Discovery app.
 * That configuration is not exposed anywhere in the Admin GraphQL API — the
 * full mutation list contains nothing matching filter/discovery/search — so
 * the facets this catalogue needs cannot be created programmatically. The
 * five that already exist on this shop were configured by hand and are
 * unusable for spec shopping: sheet_capacity offers 100 raw free-text options
 * and bin_size_container_volume offers 96, because those metafields hold
 * prose ("Up to 19 sheets (70 g/m2)") rather than numbers. throat_width and
 * shreddable_materials are not configured at all and cannot be added.
 *
 * So filtering happens here, over a full-collection index fetched from
 * templates/collection.facetdata.liquid. That template paginates at Liquid's
 * 50-product ceiling, so the index is assembled from ceil(n/50) parallel
 * requests and cached in sessionStorage for the rest of the visit.
 *
 * TRADE-OFF, STATED PLAINLY
 * Because the index is the whole collection, filtering is NOT limited to the
 * products Dawn rendered on page 1 — counts and results are correct across
 * all of it, and combinations behave. The costs are: (a) ~8 small extra
 * requests, once per visit per collection; (b) results are rendered by JS, so
 * a visitor with JS disabled keeps Dawn's normal unfiltered server-paginated
 * grid and simply sees no filter UI; (c) collections larger than MAX_INDEXED
 * skip the feature rather than fire off dozens of requests.
 *
 * Dawn's own grid is only ever hidden and re-shown — never modified — so its
 * markup, pagination and collection-jsonld ItemList stay exactly as they were.
 */
(function () {
  'use strict';

  if (window.__smFacetsReady) return;
  window.__smFacetsReady = true;

  var PAGE_SIZE = 50;          // Liquid's paginate ceiling for products
  var MAX_INDEXED = 800;       // refuse to index anything bigger than this
  var RENDER_CHUNK = 24;       // results rendered before "Show more"
  var PARAM_PREFIX = 'f.';     // our URL namespace; Shopify's is "filter."
  var DESKTOP = '(min-width: 990px)';

  /* ---------------------------------------------------------------------
   * Spec parsers.
   *
   * Every one of these was validated against all 390 live shredder products
   * before being written here; the coverage figures are measurements.
   * ------------------------------------------------------------------- */

  /* "Up to 19 sheets (70 g/m2)", "24-26 (70 g/m2) / 19-21 (80 g/m2)",
     "6-8 sheets (70 gsm); 5-6 sheets (80 gsm)"  ->  19, 26, 8
     Parses 375/390 (96.2%). Takes the first spec quoted (manufacturers list
     the 70 g/m2 figure first) and the top of any range. */
  function parseSheets(s) {
    if (!s) return null;
    var t = String(s).replace(/\([^)]*\)/g, ' ');       // drop parentheticals
    t = t.split(/[/;]/)[0];                              // first quoted spec
    t = t.replace(/\b\d+\s*g\s*\/?\s*m2?\b/gi, ' ')      // stray paper weights
         .replace(/\b\d+\s*(?:gsm|lb)\b/gi, ' ');
    var m = t.match(/\d+(?:\.\d+)?/g);
    if (!m) return null;
    return Math.max.apply(null, m.map(Number));
  }

  /* "9.2 in (230 mm)", "9.5 inch (240 mm)", "10.25 inch", "230 mm" -> inches.
     Parses 388/390 (99.5%). Prefers the imperial figure, falls back to mm. */
  function parseInches(s) {
    if (!s) return null;
    var m = String(s).match(/(\d+(?:\.\d+)?)\s*(?:in\b|inch|inches|")/i);
    if (m) return parseFloat(m[1]);
    m = String(s).match(/(\d+(?:\.\d+)?)\s*mm/i);
    if (m) return Math.round((parseFloat(m[1]) / 25.4) * 100) / 100;
    return null;
  }

  /* "10.2 gallons (38.5 litres)", "22 gallons", "35 liters" -> US gallons.
     Parses 378/390 (96.9%). */
  function parseGallons(s) {
    if (!s) return null;
    var m = String(s).match(/(\d+(?:\.\d+)?)\s*(?:us\s*)?gal/i);
    if (m) return parseFloat(m[1]);
    m = String(s).match(/(\d+(?:\.\d+)?)\s*(?:l\b|lit(?:re|er)s?\b)/i);
    if (m) return Math.round((parseFloat(m[1]) / 3.785) * 10) / 10;
    return null;
  }

  function firstTag(item, prefix) {
    var tags = item.tg || [];
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i]).toLowerCase();
      if (t.indexOf(prefix) === 0) return t.slice(prefix.length);
    }
    return null;
  }

  function allTags(item, prefix) {
    var out = [];
    var tags = item.tg || [];
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i]).toLowerCase();
      if (t.indexOf(prefix) === 0) out.push(t.slice(prefix.length));
    }
    return out;
  }

  /* DIN 66399 paper class. The metafield also carries H-/O-/E- media classes
     on a handful of machines; those are deliberately not offered as options,
     so a media-only shredder drops out when a P-level is selected rather than
     being mislabelled. P-8 does not exist in DIN 66399 and is never emitted. */
  function parseSecurity(item) {
    var m = String(item.sl || '').match(/\bP[\s-]?([1-7])\b/i);
    if (m) return 'p-' + m[1];
    var tag = firstTag(item, 'security:');
    if (tag) {
      var t = tag.match(/p-?([1-7])/i);
      if (t) return 'p-' + t[1];
    }
    return null;
  }

  function parseCut(item) {
    var v = String(item.ct || '').toLowerCase();
    if (!v) v = String(firstTag(item, 'cut:') || '').toLowerCase();
    if (!v) return null;
    if (v.indexOf('micro') > -1) return 'micro';
    if (v.indexOf('cross') > -1) return 'cross';
    if (v.indexOf('strip') > -1) return 'strip';
    if (v.indexOf('particle') > -1) return 'particle';
    return null;
  }

  var MATERIAL_TESTS = {
    staples: /staple/i,
    clips: /paper\s*clip/i,
    cards: /credit\s*card|chip\s*card/i,
    optical: /\bcds?\b|dvd|blu-?ray|optical/i,
    drives: /hard\s*(?:disk|drive)|\bhdd\b|\bssd\b|solid[\s-]*state/i
  };

  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function band(value, bands) {
    if (value === null || value === undefined || isNaN(value)) return [];
    for (var i = 0; i < bands.length; i++) {
      if (value >= bands[i].min && value < bands[i].max) return [bands[i].id];
    }
    return [];
  }

  var SHEET_BANDS = [
    { id: '1-10', label: '1 – 10 sheets', min: 0, max: 11 },
    { id: '11-20', label: '11 – 20 sheets', min: 11, max: 21 },
    { id: '21-30', label: '21 – 30 sheets', min: 21, max: 31 },
    { id: '31-50', label: '31 – 50 sheets', min: 31, max: 51 },
    { id: '51plus', label: '51+ sheets', min: 51, max: Infinity }
  ];
  var THROAT_BANDS = [
    { id: 'u9', label: 'Under 9"', min: 0, max: 9 },
    { id: '9-10', label: '9" – 10"', min: 9, max: 10 },
    { id: '10-12', label: '10" – 12"', min: 10, max: 12 },
    { id: '12-16', label: '12" – 16"', min: 12, max: 16 },
    { id: '16plus', label: '16" and wider', min: 16, max: Infinity }
  ];
  var BIN_BANDS = [
    { id: 'u10', label: 'Under 10 gal', min: 0, max: 10 },
    { id: '10-20', label: '10 – 20 gal', min: 10, max: 20 },
    { id: '20-35', label: '20 – 35 gal', min: 20, max: 35 },
    { id: '35-60', label: '35 – 60 gal', min: 35, max: 60 },
    { id: '60plus', label: '60 gal and up', min: 60, max: Infinity }
  ];
  /* Price bands are in cents, matching Liquid's product.price. */
  var PRICE_BANDS = [
    { id: 'u500', label: 'Under $500', min: 0, max: 50000 },
    { id: '500-1k', label: '$500 – $1,000', min: 50000, max: 100000 },
    { id: '1k-2k5', label: '$1,000 – $2,500', min: 100000, max: 250000 },
    { id: '2k5-5k', label: '$2,500 – $5,000', min: 250000, max: 500000 },
    { id: '5k-10k', label: '$5,000 – $10,000', min: 500000, max: 1000000 },
    { id: '10kplus', label: '$10,000 and up', min: 1000000, max: Infinity }
  ];

  var FACETS = [
    {
      key: 'sec', label: 'Security level',
      help: 'DIN 66399 paper security class — the higher the number, the smaller the particle.',
      options: [
        { id: 'p-1', label: 'P-1 — General internal' },
        { id: 'p-2', label: 'P-2 — Internal' },
        { id: 'p-3', label: 'P-3 — Confidential' },
        { id: 'p-4', label: 'P-4 — Sensitive' },
        { id: 'p-5', label: 'P-5 — Secret' },
        { id: 'p-6', label: 'P-6 — High security' },
        { id: 'p-7', label: 'P-7 — Top secret' }
      ],
      get: function (i) { var v = parseSecurity(i); return v ? [v] : []; }
    },
    {
      key: 'cut', label: 'Cut type',
      options: [
        { id: 'strip', label: 'Strip cut' },
        { id: 'cross', label: 'Cross cut' },
        { id: 'micro', label: 'Micro cut' },
        { id: 'particle', label: 'Particle cut' }
      ],
      get: function (i) { var v = parseCut(i); return v ? [v] : []; }
    },
    {
      key: 'sheets', label: 'Sheet capacity',
      help: 'Sheets per pass at 70 g/m² unless the manufacturer states otherwise.',
      options: SHEET_BANDS,
      get: function (i) { return band(parseSheets(i.sc), SHEET_BANDS); }
    },
    {
      key: 'throat', label: 'Throat width',
      help: 'Widest sheet the feed opening accepts.',
      options: THROAT_BANDS,
      get: function (i) { return band(parseInches(i.tw), THROAT_BANDS); }
    },
    {
      key: 'bin', label: 'Bin volume',
      options: BIN_BANDS,
      get: function (i) { return band(parseGallons(i.bv), BIN_BANDS); }
    },
    {
      key: 'shreds', label: 'Also shreds',
      options: [
        { id: 'staples', label: 'Staples' },
        { id: 'clips', label: 'Paper clips' },
        { id: 'cards', label: 'Credit / chip cards' },
        { id: 'optical', label: 'CDs / DVDs' },
        { id: 'drives', label: 'Hard drives / SSDs' }
      ],
      get: function (i) {
        if (!i.sm) return [];
        var out = [];
        for (var k in MATERIAL_TESTS) {
          if (MATERIAL_TESTS[k].test(i.sm)) out.push(k);
        }
        return out;
      }
    },
    {
      key: 'use', label: 'Best for',
      options: [
        { id: 'personal', label: 'Personal / deskside' },
        { id: 'small-office', label: 'Small office' },
        { id: 'medium-office', label: 'Medium office' },
        { id: 'large-office', label: 'Large office' },
        { id: 'industrial', label: 'Industrial' },
        { id: 'government', label: 'Government / NSA' }
      ],
      get: function (i) { return allTags(i, 'use:'); }
    },
    {
      key: 'brand', label: 'Brand',
      dynamic: true,                        // options derived from the index
      get: function (i) { return i.v ? [slug(i.v)] : []; }
    },
    {
      key: 'price', label: 'Price',
      options: PRICE_BANDS,
      get: function (i) { return band(i.pr, PRICE_BANDS); }
    }
  ];

  var SORTS = [
    { id: 'rel', label: 'Featured' },
    { id: 'price-asc', label: 'Price: low to high' },
    { id: 'price-desc', label: 'Price: high to low' },
    { id: 'title-asc', label: 'Name: A – Z' },
    { id: 'title-desc', label: 'Name: Z – A' }
  ];

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function isDesktop() { return window.matchMedia(DESKTOP).matches; }

  /* ===================================================================== */

  function SmFacets(root) {
    this.root = root;
    this.handle = root.getAttribute('data-collection-handle');
    this.total = parseInt(root.getAttribute('data-collection-count'), 10) || 0;
    this.gridContainer = document.getElementById('ProductGridContainer');
    this.index = null;
    this.loading = null;
    this.state = {};
    this.expanded = {};         // which dropdowns/accordions are open
    this.sort = 'rel';
    this.shown = RENDER_CHUNK;
    this.built = false;

    this.els = {
      groups: root.querySelector('[data-smf-groups]'),
      status: root.querySelector('[data-smf-status]'),
      active: root.querySelector('[data-smf-active]'),
      results: root.querySelector('[data-smf-results]'),
      grid: root.querySelector('[data-smf-grid]'),
      more: root.querySelector('[data-smf-more]'),
      empty: root.querySelector('[data-smf-empty]'),
      drawer: root.querySelector('[data-smf-drawer]'),
      openBtn: root.querySelector('[data-smf-open]'),
      openCount: root.querySelector('[data-smf-open-count]'),
      applyBtn: root.querySelector('[data-smf-apply]'),
      sortWrap: root.querySelector('[data-smf-sortwrap]'),
      sortSel: root.querySelector('[data-smf-sort]'),
      countLive: root.querySelector('[data-smf-count]')
    };

    this.bind();
    this.readUrl();

    if (this.total > MAX_INDEXED) {
      this.root.hidden = true;
      return;
    }

    var self = this;
    if (this.hasActive()) {
      /* Arrived on a shared or bookmarked filtered URL — build immediately. */
      this.ensureIndex().then(function () { self.build(); self.apply(); });
    } else {
      var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
      idle(function () {
        self.ensureIndex().then(function () { self.build(); self.refresh(); });
      });
    }
  }

  SmFacets.prototype.hasActive = function () {
    for (var k in this.state) { if (this.state[k] && this.state[k].length) return true; }
    return false;
  };

  SmFacets.prototype.activeCount = function () {
    var n = 0;
    for (var k in this.state) { n += (this.state[k] || []).length; }
    return n;
  };

  /* ---------------- index loading ---------------- */

  SmFacets.prototype.cacheKey = function () {
    return 'smfx:2:' + this.handle + ':' + this.total;
  };

  SmFacets.prototype.ensureIndex = function () {
    if (this.index) return Promise.resolve(this.index);
    if (this.loading) return this.loading;

    var self = this;
    var cached = null;
    try { cached = sessionStorage.getItem(this.cacheKey()); } catch (e) { /* private mode */ }
    if (cached) {
      try {
        this.index = JSON.parse(cached);
        this.precompute(this.index);
        return Promise.resolve(this.index);
      } catch (e) { /* fall through and refetch */ }
    }

    this.setStatus('Loading filters…');
    var base = '/collections/' + encodeURIComponent(this.handle) + '?view=facetdata';

    function getPage(p) {
      return fetch(base + '&page=' + p, { headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (t) { return JSON.parse(t); });
    }

    this.loading = getPage(1)
      .then(function (first) {
        var pages = Math.min(first.pages || 1, Math.ceil(MAX_INDEXED / PAGE_SIZE));
        if (pages <= 1) return [first];
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(getPage(p));
        return Promise.all(rest).then(function (others) { return [first].concat(others); });
      })
      .then(function (pagesData) {
        var items = [];
        pagesData
          .sort(function (a, b) { return a.page - b.page; })
          .forEach(function (pd) { items = items.concat(pd.items || []); });
        self.index = items;
        try {
          sessionStorage.setItem(self.cacheKey(), JSON.stringify(items));
        } catch (e) { /* quota or private mode — the index still works */ }
        self.precompute(items);
        self.setStatus('');
        return items;
      })
      .catch(function (err) {
        self.setStatus('Filters could not be loaded. Please reload the page.');
        if (window.console) console.warn('[sm-facets] index load failed', err);
        self.index = [];
        return [];
      });

    return this.loading;
  };

  /* Resolve facet membership once per product. Filtering then costs an array
     lookup per product instead of re-running every regex on every click. */
  SmFacets.prototype.precompute = function (items) {
    items.forEach(function (it) {
      if (it._f) return;
      it._f = {};
      FACETS.forEach(function (f) { it._f[f.key] = f.get(it); });
    });
  };

  SmFacets.prototype.setStatus = function (msg) {
    if (!this.els.status) return;
    this.els.status.textContent = msg || '';
    this.els.status.hidden = !msg;
  };

  /* ---------------- URL state ---------------- */

  SmFacets.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var self = this;
    this.state = {};
    FACETS.forEach(function (f) {
      var raw = params.get(PARAM_PREFIX + f.key);
      self.state[f.key] = raw ? raw.split(',').filter(Boolean) : [];
    });
    var s = params.get(PARAM_PREFIX + 'sort');
    this.sort = SORTS.some(function (x) { return x.id === s; }) ? s : 'rel';
    if (this.els.sortSel) this.els.sortSel.value = this.sort;
  };

  SmFacets.prototype.writeUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var self = this;
    FACETS.forEach(function (f) {
      var v = self.state[f.key] || [];
      if (v.length) params.set(PARAM_PREFIX + f.key, v.join(','));
      else params.delete(PARAM_PREFIX + f.key);
    });
    if (this.sort !== 'rel') params.set(PARAM_PREFIX + 'sort', this.sort);
    else params.delete(PARAM_PREFIX + 'sort');

    var qs = params.toString();
    history.pushState({ smf: 1 }, '', window.location.pathname + (qs ? '?' + qs : ''));
  };

  /* ---------------- matching ---------------- */

  /* Match an item against every facet except `skipKey`. Passing a key is how
     the counts stay honest: the number beside "P-4" is how many products you
     would get if you ticked it, given everything already ticked elsewhere. */
  SmFacets.prototype.matches = function (item, skipKey) {
    for (var k in this.state) {
      if (k === skipKey) continue;
      var sel = this.state[k];
      if (!sel || !sel.length) continue;
      var have = item._f[k] || [];
      var hit = false;
      for (var i = 0; i < sel.length; i++) {
        if (have.indexOf(sel[i]) > -1) { hit = true; break; }
      }
      if (!hit) return false;
    }
    return true;
  };

  SmFacets.prototype.results = function () {
    if (!this.index) return [];
    var self = this;
    return this.sortItems(this.index.filter(function (it) { return self.matches(it, null); }));
  };

  SmFacets.prototype.sortItems = function (arr) {
    var s = this.sort;
    if (s === 'rel') return arr;
    return arr.slice().sort(function (a, b) {
      if (s === 'price-asc') return a.pr - b.pr;
      if (s === 'price-desc') return b.pr - a.pr;
      if (s === 'title-asc') return a.t.localeCompare(b.t);
      if (s === 'title-desc') return b.t.localeCompare(a.t);
      return 0;
    });
  };

  /* Options present anywhere in this collection, each counted under the other
     active facets. Options no product can satisfy are dropped entirely — a
     filter that returns nothing is worse than no filter. */
  SmFacets.prototype.optionsFor = function (facet) {
    var self = this;
    var universe = {};
    this.index.forEach(function (it) {
      (it._f[facet.key] || []).forEach(function (id) { universe[id] = true; });
    });

    var counts = {};
    this.index.forEach(function (it) {
      if (!self.matches(it, facet.key)) return;
      (it._f[facet.key] || []).forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
    });

    var defs;
    if (facet.dynamic) {
      var seen = {};
      this.index.forEach(function (it) {
        (it._f[facet.key] || []).forEach(function (id) {
          if (!seen[id]) seen[id] = { id: id, label: it.v || id };
        });
      });
      defs = Object.keys(seen).map(function (k) { return seen[k]; })
        .sort(function (a, b) { return a.label.localeCompare(b.label); });
    } else {
      defs = facet.options;
    }

    return defs
      .filter(function (o) { return universe[o.id]; })
      .map(function (o) {
        return {
          id: o.id,
          label: o.label,
          count: counts[o.id] || 0,
          checked: (self.state[facet.key] || []).indexOf(o.id) > -1
        };
      });
  };

  /* ---------------- building / refreshing the UI ----------------
   * build() writes the markup once. refresh() only updates counts, checked
   * and disabled state, so ticking a box never destroys the node the visitor
   * is standing on — focus and the open dropdown both survive.
   * ------------------------------------------------------------- */

  SmFacets.prototype.build = function () {
    if (!this.index || !this.els.groups || this.built) return;
    var self = this;
    var openByDefault = !isDesktop();
    var html = '';

    FACETS.forEach(function (f) {
      var opts = self.optionsFor(f);
      if (!opts.length) return;
      if (self.expanded[f.key] === undefined) self.expanded[f.key] = openByDefault;
      var open = self.expanded[f.key];
      var panelId = 'SmfPanel-' + f.key;

      html += '<div class="smf-group' + (open ? '' : ' smf-group--collapsed') +
              '" data-smf-group="' + esc(f.key) + '">';
      html += '<h3 class="smf-group__head">' +
              '<button type="button" class="smf-group__toggle" aria-expanded="' + open +
              '" aria-controls="' + panelId + '">' +
              '<span class="smf-group__label">' + esc(f.label) + '</span>' +
              '<span class="smf-group__badge" data-smf-badge hidden></span>' +
              '<span class="smf-group__chev" aria-hidden="true"></span>' +
              '</button></h3>';
      html += '<div class="smf-group__panel" id="' + panelId + '">';
      if (f.help) html += '<p class="smf-group__help">' + esc(f.help) + '</p>';
      html += '<ul class="smf-opts">';
      opts.forEach(function (o) {
        var id = 'smf-' + f.key + '-' + slug(o.id);
        html += '<li class="smf-opt">' +
          '<input type="checkbox" id="' + esc(id) + '" class="smf-opt__input"' +
          ' data-smf-key="' + esc(f.key) + '" data-smf-val="' + esc(o.id) + '">' +
          '<label class="smf-opt__label" for="' + esc(id) + '">' +
          '<span class="smf-opt__text">' + esc(o.label) + '</span>' +
          '<span class="smf-opt__count" data-smf-optcount>' + o.count + '</span>' +
          '</label></li>';
      });
      html += '</ul></div></div>';
    });

    this.els.groups.innerHTML = html;
    this.built = true;
  };

  SmFacets.prototype.refresh = function () {
    if (!this.built) return;
    var self = this;

    FACETS.forEach(function (f) {
      var group = self.els.groups.querySelector('[data-smf-group="' + f.key + '"]');
      if (!group) return;
      var opts = self.optionsFor(f);
      var byId = {};
      opts.forEach(function (o) { byId[o.id] = o; });

      group.querySelectorAll('.smf-opt__input').forEach(function (input) {
        var o = byId[input.getAttribute('data-smf-val')];
        var li = input.closest('.smf-opt');
        if (!o) { li.hidden = true; return; }
        li.hidden = false;
        input.checked = o.checked;
        input.disabled = o.count === 0 && !o.checked;
        li.classList.toggle('smf-opt--empty', input.disabled);
        var c = li.querySelector('[data-smf-optcount]');
        if (c) c.textContent = o.count;
      });

      var n = (self.state[f.key] || []).length;
      var badge = group.querySelector('[data-smf-badge]');
      if (badge) { badge.textContent = n; badge.hidden = n === 0; }
    });

    this.renderActive();
    this.renderCount();
  };

  SmFacets.prototype.renderActive = function () {
    if (!this.els.active) return;
    var self = this;
    var chips = [];

    FACETS.forEach(function (f) {
      (self.state[f.key] || []).forEach(function (v) {
        var label = v;
        if (f.dynamic) {
          for (var i = 0; i < self.index.length; i++) {
            if ((self.index[i]._f[f.key] || []).indexOf(v) > -1) { label = self.index[i].v; break; }
          }
        } else {
          var def = f.options.filter(function (o) { return o.id === v; })[0];
          if (def) label = def.label;
        }
        chips.push(
          '<button type="button" class="smf-chip" data-smf-remove-key="' + esc(f.key) +
          '" data-smf-remove-val="' + esc(v) + '">' +
          '<span class="smf-chip__facet">' + esc(f.label) + ':</span> ' + esc(label) +
          '<span class="smf-chip__x" aria-hidden="true">&times;</span>' +
          '<span class="visually-hidden"> — remove this filter</span></button>'
        );
      });
    });

    if (chips.length) {
      this.els.active.innerHTML = chips.join('') +
        '<button type="button" class="smf-chip smf-chip--clear" data-smf-clearall>Clear all</button>';
      this.els.active.hidden = false;
    } else {
      this.els.active.innerHTML = '';
      this.els.active.hidden = true;
    }
  };

  SmFacets.prototype.renderCount = function () {
    if (!this.index) return;
    var n = this.results().length;
    if (this.els.countLive) {
      this.els.countLive.textContent = this.hasActive()
        ? n + ' of ' + this.index.length + ' products'
        : this.index.length + ' products';
    }
    if (this.els.applyBtn) {
      this.els.applyBtn.textContent = 'Show ' + n + (n === 1 ? ' result' : ' results');
    }
    if (this.els.openCount) {
      var a = this.activeCount();
      this.els.openCount.textContent = a ? ' (' + a + ')' : '';
    }
  };

  SmFacets.prototype.card = function (it) {
    var img = it.img
      ? '<img class="smf-card__img" src="' + esc(it.img) + '" alt="' + esc(it.alt) +
        '" loading="lazy" width="400" height="400">'
      : '<span class="smf-card__img" aria-hidden="true"></span>';
    return '<li class="smf-card">' +
      '<a class="smf-card__link" href="' + esc(it.u) + '">' +
      '<span class="smf-card__media">' + img + '</span>' +
      '<span class="smf-card__body">' +
      (it.v ? '<span class="smf-card__vendor">' + esc(it.v) + '</span>' : '') +
      '<span class="smf-card__title">' + esc(it.t) + '</span>' +
      '<span class="smf-card__price">' + esc(it.pm) +
      (it.cap ? ' <s class="smf-card__was">' + esc(it.cap) + '</s>' : '') +
      '</span></span></a></li>';
  };

  SmFacets.prototype.renderResults = function () {
    var items = this.results();
    this.els.grid.innerHTML = items.slice(0, this.shown).map(this.card).join('');
    this.els.empty.hidden = items.length > 0;
    this.els.more.hidden = items.length <= this.shown;
    if (!this.els.more.hidden) {
      this.els.more.textContent = 'Show more (' + (items.length - this.shown) + ' remaining)';
    }
  };

  /* Swap between Dawn's server-rendered grid and ours. Dawn's markup is only
     hidden, never touched, so its pagination and JSON-LD survive intact. */
  SmFacets.prototype.setFiltered = function (on) {
    if (this.gridContainer) this.gridContainer.hidden = !!on;
    if (this.els.results) this.els.results.hidden = !on;
    if (this.els.sortWrap) this.els.sortWrap.hidden = !on;
    /* While our filter owns the results, Dawn's own sort form would navigate
       away and drop our params, so it steps aside for the duration. */
    var dawnControls = document.querySelector('.facet-filters, .collection-filters');
    if (dawnControls) dawnControls.classList.toggle('smf-hidden', !!on);
    this.root.classList.toggle('smf--active', !!on);
  };

  SmFacets.prototype.apply = function (keepShown) {
    if (!this.index) return;
    if (!keepShown) this.shown = RENDER_CHUNK;
    var active = this.hasActive();
    this.setFiltered(active);
    if (active) this.renderResults();
    this.refresh();
  };

  /* ---------------- events ---------------- */

  SmFacets.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (e) {
      var input = e.target.closest ? e.target.closest('.smf-opt__input') : null;
      if (input) {
        var key = input.getAttribute('data-smf-key');
        var val = input.getAttribute('data-smf-val');
        var cur = (self.state[key] || []).slice();
        if (input.checked) { if (cur.indexOf(val) === -1) cur.push(val); }
        else { cur = cur.filter(function (v) { return v !== val; }); }
        self.state[key] = cur;
        self.writeUrl();
        self.apply();
        return;
      }
      if (e.target === self.els.sortSel) {
        self.sort = self.els.sortSel.value;
        self.writeUrl();
        self.apply();
      }
    });

    this.root.addEventListener('click', function (e) {
      var t = e.target;
      var toggle = t.closest('.smf-group__toggle');
      if (toggle) {
        var group = toggle.closest('.smf-group');
        var key = group.getAttribute('data-smf-group');
        var open = toggle.getAttribute('aria-expanded') === 'true';
        if (!open && isDesktop()) self.closeAllGroups();
        toggle.setAttribute('aria-expanded', String(!open));
        group.classList.toggle('smf-group--collapsed', open);
        self.expanded[key] = !open;
        return;
      }
      var chip = t.closest('[data-smf-remove-key]');
      if (chip) {
        var k = chip.getAttribute('data-smf-remove-key');
        var v = chip.getAttribute('data-smf-remove-val');
        self.state[k] = (self.state[k] || []).filter(function (x) { return x !== v; });
        self.writeUrl();
        self.apply();
        return;
      }
      if (t.closest('[data-smf-clearall]')) {
        FACETS.forEach(function (f) { self.state[f.key] = []; });
        self.writeUrl();
        self.apply();
        self.closeDrawer();
        return;
      }
      if (t.closest('[data-smf-more]')) {
        self.shown += RENDER_CHUNK;
        self.renderResults();
        return;
      }
      if (t.closest('[data-smf-open]')) { self.openDrawer(); return; }
      if (t.closest('[data-smf-close]') || t.closest('[data-smf-apply]') ||
          t.hasAttribute('data-smf-scrim')) {
        self.closeDrawer();
      }
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.root.classList.contains('smf--drawer-open')) {
        e.stopPropagation();
        self.closeDrawer();
      } else if (isDesktop()) {
        var open = self.root.querySelector('.smf-group__toggle[aria-expanded="true"]');
        if (open) { e.stopPropagation(); self.closeAllGroups(); open.focus(); }
      }
    });

    /* A click anywhere else closes an open desktop dropdown. */
    document.addEventListener('click', function (e) {
      if (!isDesktop()) return;
      if (self.root.contains(e.target)) return;
      self.closeAllGroups();
    });

    window.addEventListener('popstate', function () {
      self.readUrl();
      if (self.index) { self.build(); self.apply(); }
      else self.ensureIndex().then(function () { self.build(); self.apply(); });
    });
  };

  SmFacets.prototype.closeAllGroups = function () {
    var self = this;
    this.root.querySelectorAll('.smf-group').forEach(function (g) {
      g.classList.add('smf-group--collapsed');
      var b = g.querySelector('.smf-group__toggle');
      if (b) b.setAttribute('aria-expanded', 'false');
      self.expanded[g.getAttribute('data-smf-group')] = false;
    });
  };

  SmFacets.prototype.openDrawer = function () {
    var self = this;
    this.ensureIndex().then(function () {
      self.build();
      self.refresh();
      self.root.classList.add('smf--drawer-open');
      if (self.els.openBtn) self.els.openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('smf-noscroll');
      var first = self.els.drawer.querySelector('button, input:not([disabled])');
      if (first) first.focus();
    });
  };

  SmFacets.prototype.closeDrawer = function () {
    if (!this.root.classList.contains('smf--drawer-open')) return;
    this.root.classList.remove('smf--drawer-open');
    document.documentElement.classList.remove('smf-noscroll');
    if (this.els.openBtn) {
      this.els.openBtn.setAttribute('aria-expanded', 'false');
      this.els.openBtn.focus();
    }
  };

  function init() {
    var root = document.querySelector('[data-sm-facets]');
    if (!root) return;
    var sel = root.querySelector('[data-smf-sort]');
    if (sel && !sel.options.length) {
      SORTS.forEach(function (s) {
        var o = document.createElement('option');
        o.value = s.id;
        o.textContent = s.label;
        sel.appendChild(o);
      });
    }
    window.smFacets = new SmFacets(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
